import React from "react";
import { FieldArray, FormikProps } from "formik";
import ATMLoadingButton from "src/components/UI/atoms/ATMLoadingButton/ATMLoadingButton";
import ATMTextField from "src/components/UI/atoms/formFields/ATMTextField/ATMTextField";
import { BatchRecordingFormValues } from "src/models/BatchRecording.model";
import ATMDatePicker from "src/components/UI/atoms/formFields/ATMDatePicker/ATMDatePicker";
import { AiOutlinePlus } from "react-icons/ai";
import { MdDelete } from "react-icons/md";
import ATMFilePickerWrapper from "src/components/UI/atoms/formFields/ATMFileUploader/ATMFileUploaderWrapper";
import ATMTextArea from "src/components/UI/atoms/formFields/ATMTextArea/ATMTextArea";
import ATMCheckbox from "src/components/UI/atoms/formFields/ATMCheckbox/ATMCheckbox";

type Props = {
  formikProps: FormikProps<BatchRecordingFormValues>;
  onClose: () => void;
  formType: "ADD" | "UPDATE";
};

const BatchRecordingForm = ({ formikProps, onClose, formType }: Props) => {
  const { values, setFieldValue, isSubmitting, handleBlur } = formikProps;

  return (
    <div className="p-4 flex flex-col gap-6">
      <div className="flex justify-between items-center">
        <div className="text-xl font-medium">
          {" "}
          {formType === "ADD" ? "Add Recording" : "Update Recording"}{" "}
        </div>
        <button type="button" className="hover:bg-hover" onClick={onClose}>
          Cancel
        </button>
      </div>
      <div className="flex flex-col gap-4">
        {/* Date */}
        <div className="">
          <ATMDatePicker
            name="date"
            value={values?.date || null}
            onChange={(newValue) => setFieldValue("date", newValue)}
            label="Date"
          />
        </div>
        <div className="border-l-[3px] border-primary-dark px-3 py-1 z-10 mt-2  font font-medium text-[18px] sticky top-0 bg-white  text-primary-dark">
          Recordings
        </div>
        <div className="mt-1">
          <FieldArray
            name="recordings"
            render={({ push, remove }) => (
              <>
                {values?.recordings?.map((doc: any, index: any) => {
                  return (
                    <div key={index}>
                      <div className="flex justify-end mt-3">
                        <MdDelete
                          color="#F70913"
                          size={30}
                          className="pl-1 cursor-pointer"
                          onClick={() => {
                            remove(index);
                          }}
                        />
                      </div>
                      <div className="grid  md:grid-cols-2 xs:grid-cols-1 xl:gap-6 gap-4 ">
                        <div>
                          <ATMTextField
                            name={`recordings[${index}].title`}
                            value={values?.recordings?.[index]?.title}
                            onChange={(e) =>
                              setFieldValue(
                                `recordings[${index}].title`,
                                e.target.value
                              )
                            }
                            label="Title"
                            placeholder="Enter Title"
                            onBlur={handleBlur}
                          />
                        </div>
                        <div>
                          <ATMTextField
                            name={`recordings[${index}].url`}
                            value={values?.recordings?.[index]?.url}
                            onChange={(e) =>
                              setFieldValue(
                                `recordings[${index}].url`,
                                e.target.value
                              )
                            }
                            label="Recording URL"
                            placeholder="Enter Recording URL"
                            onBlur={handleBlur}
                          />
                          <div className="mt-4">
                            <ATMCheckbox
                              extraClasses="text-[12px]"
                              inputClasses="h-[12px]"
                              label="Show Recording"
                              checked={
                                values?.recordings?.[index]?.isShowRecording
                              }
                              onChange={(checked) =>
                                setFieldValue(
                                  `recordings[${index}].isShowRecording`,
                                  checked
                                )
                              }
                            />
                          </div>
                        </div>
                        {values?.recordings?.[index]?.isShowRecording ? (
                          <div>
                            <iframe
                              className="overflow-hidden rounded-md aspect-video"
                              src={values?.recordings?.[index]?.url}
                              title={`recording ${index} `}
                              allowFullScreen
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
                            />
                          </div>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
                <div className="flex justify-end my-4">
                  <p
                    className="text-link font-semibold text-base text-[16px] max-[1024px]:text-sm flex items-center cursor-pointer"
                    onClick={() =>
                      push({
                        title: "",
                        url: "",
                      })
                    }
                  >
                    <AiOutlinePlus size={18} color="#0064C4" className="mr-2" />
                    Add Recording
                  </p>
                </div>
              </>
            )}
          />
        </div>
        <div className="border-l-[3px] border-primary-dark px-3 py-1 z-10  font font-medium text-[18px] sticky top-0 bg-white  text-primary-dark">
          Files
        </div>
        <div className="mt-1">
          <FieldArray
            name="fileUrl"
            render={({ push, remove }) => (
              <>
                {values?.fileUrl?.map((doc: any, index: any) => {
                  return (
                    <div key={index}>
                      <div className="flex justify-end mt-3">
                        <MdDelete
                          color="#F70913"
                          size={30}
                          className="pl-1 cursor-pointer"
                          onClick={() => {
                            remove(index);
                          }}
                        />
                      </div>
                      <div>
                        <div>
                          <ATMFilePickerWrapper
                            name={`fileUrl[${index}].fileName`}
                            accept="image/*,.pdf"
                            label="File"
                            placeholder="select file"
                            selectedFile={values?.fileUrl?.[index]?.fileName}
                            onSelect={(imgUrl) => {
                              setFieldValue(
                                `fileUrl[${index}].fileName`,
                                imgUrl
                              );
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div className="flex justify-end my-4">
                  <p
                    className="text-link font-semibold text-base text-[16px] max-[1024px]:text-sm flex items-center cursor-pointer"
                    onClick={() =>
                      push({
                        fileName: "",
                      })
                    }
                  >
                    <AiOutlinePlus size={18} color="#0064C4" className="mr-2" />
                    Add File
                  </p>
                </div>
              </>
            )}
          />
        </div>
        {/* Add Remark Input */}
        <ATMTextArea
          placeholder="Enter Description"
          label="Description"
          name="description"
          required
          value={values?.description}
          onChange={(newValue) => {
            setFieldValue("description", newValue);
          }}
        />
      </div>
      <div>
        <ATMLoadingButton isLoading={isSubmitting} type="submit">
          Save
        </ATMLoadingButton>
      </div>
    </div>
  );
};

export default BatchRecordingForm;
